/**
 * Username preference.
 *
 * The username is a *display value only*: the server authenticates the
 * HttpOnly session cookie and identifies the participant by an opaque
 * `IdentityId`. Storing the name locally therefore stores no credential, and
 * it deliberately survives a room reset (which invalidates sessions) so a
 * returning user is only asked for the invite link again, not for their name.
 *
 * Policy (master plan default): trim, 1–32 Unicode code points, duplicates
 * allowed — the participant list disambiguates them with a short identity
 * suffix. Rendering is always as text, never as markup.
 */

const STORAGE_KEY = 'watchalong.username';

export const USERNAME_MAX_LENGTH = 32;

export type UsernameCheck =
  | { readonly ok: true; readonly username: string }
  | { readonly ok: false; readonly message: string };

/** Trims and length-checks a candidate name, counting code points not UTF-16 units. */
export function checkUsername(raw: string): UsernameCheck {
  const username = raw.trim();
  if (username === '') {
    return { ok: false, message: 'Enter a name so other people can see who you are.' };
  }
  if ([...username].length > USERNAME_MAX_LENGTH) {
    return {
      ok: false,
      message: `Names are at most ${USERNAME_MAX_LENGTH} characters.`,
    };
  }
  // Control characters would render as invisible or layout-breaking glyphs.
  if (/[\p{Cc}\p{Cf}]/u.test(username)) {
    return { ok: false, message: 'Names cannot contain control characters.' };
  }
  return { ok: true, username };
}

/** The remembered name, or `null`. Storage failures are not user errors. */
export function loadUsername(): string | null {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode or disabled storage: the user is simply asked each time.
    return null;
  }
  if (stored === null) return null;
  const checked = checkUsername(stored);
  return checked.ok ? checked.username : null;
}

export function saveUsername(username: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, username);
  } catch {
    // Preference persistence is best-effort; the session still works.
  }
}

/** Short identity suffix used to disambiguate duplicate display names. */
export function identitySuffix(identity: string): string {
  return identity.slice(0, 4);
}
